import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { useNotification } from '../hooks/useNotification';
import { Button } from '../components/ui/button';
import { Plus, Filter, Search, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Task, TaskStatus } from 'public-contracts';
import taskService from '../services/taskService';
import { usersService, User } from '../services/usersService';
import { KanbanColumn } from './components/KanbanColumn';
import { TaskDialog } from './components/TaskDialog';

export const ModernKanbanBoard: React.FC = () => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();
  const { notify } = useNotification();

  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [initialStatus, setInitialStatus] = useState<TaskStatus | undefined>();

  const loadData = async () => {
    setLoading(true);
    try {
      const [tasksData, usersData] = await Promise.all([
        taskService.getTasks(),
        usersService.getUsers({ size: 1000 })
      ]);
      setTasks(tasksData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load data:', error);
      notify(translate('pos.project_management.messages.load_error', 'Failed to load data'), {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleTaskDrop = async (taskId: string, newStatus: TaskStatus) => {
    // Optimistic update
    const previousTasks = [...tasks];
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );

    try {
      await taskService.updateTaskStatus(taskId, newStatus);
      notify(translate('pos.project_management.messages.status_updated', 'Task status updated'), {
        type: 'success',
      });
    } catch (error) {
      console.error('Failed to update task status:', error);
      setTasks(previousTasks); // Revert on failure
      notify(translate('pos.project_management.messages.update_error', 'Failed to update task status'), {
        type: 'error',
      });
    }
  };

  const handleCreateTask = (status?: TaskStatus) => {
    setSelectedTask(null);
    setInitialStatus(status);
    setDialogOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setInitialStatus(undefined);
    setDialogOpen(true);
  };

  const handleSaveTask = async (taskData: Partial<Task>) => {
    try {
      if (selectedTask) {
        await taskService.updateTask(selectedTask.id, taskData);
        notify(translate('pos.project_management.messages.task_updated', 'Task updated successfully'), {
          type: 'success',
        });
      } else {
        await taskService.createTask(taskData);
        notify(translate('pos.project_management.messages.task_created', 'Task created successfully'), {
          type: 'success',
        });
      }
      loadData();
    } catch (error) {
      console.error('Failed to save task:', error);
      notify(translate('pos.project_management.messages.save_error', 'Failed to save task'), {
        type: 'error',
      });
      throw error;
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    try {
      await taskService.deleteTask(selectedTask.id);
      notify(translate('pos.project_management.messages.task_deleted', 'Task deleted successfully'), {
        type: 'success',
      });
      loadData();
    } catch (error) {
      console.error('Failed to delete task:', error);
      notify(translate('pos.project_management.messages.delete_error', 'Failed to delete task'), {
        type: 'error',
      });
      throw error;
    }
  };

  const filteredTasks = tasks.filter((task) =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    task.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tasksByStatus = (status: TaskStatus) =>
    filteredTasks.filter((task) => task.status === status)
      .sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b bg-background flex shrink-0 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {translate('pos.project_management.kanban_board', 'Kanban Board')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {translate(
              'pos.project_management.kanban_subtitle',
              'Manage tasks and track progress'
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder={translate('common.search', 'Search...')}
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            {translate('common.filter', 'Filter')}
          </Button>
          {hasPermission('project_management', 'create') && (
            <Button onClick={() => handleCreateTask()}>
              <Plus className="h-4 w-4 mr-2" />
              {translate('pos.project_management.create_task', 'Create Task')}
            </Button>
          )}
        </div>
      </div>

      {/* Kanban Board Content */}
      <div className="flex-1 overflow-x-auto p-6 bg-slate-50/50">
        {loading && tasks.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="flex gap-6 h-full min-h-0">
            {Object.values(TaskStatus).map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                tasks={tasksByStatus(status)}
                users={users}
                onTaskDrop={handleTaskDrop}
                onAddTask={handleCreateTask}
                onEditTask={handleEditTask}
              />
            ))}
          </div>
        )}
      </div>

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={selectedTask}
        users={users}
        initialStatus={initialStatus}
        onSave={handleSaveTask}
        onDelete={
          // Assuming 'delete' permission is required. If 'project_management' is not the correct resource, check 'tasks'.
          // Since we use 'project_management' for create, stick with it.
          hasPermission('project_management', 'create') ? handleDeleteTask : undefined
        }
      />
    </div>
  );
};

export default ModernKanbanBoard;
